import React from 'react';
import { every } from 'lodash';
import cs from 'classnames';
import { Trans } from 'react-i18next';
import { Input } from 'semantic-ui-react';
import Checkbox from '@material-ui/core/Checkbox';
import CircleCheckedFilled from '@material-ui/icons/CheckCircle';
import CircleUnchecked from '@material-ui/icons/RadioButtonUnchecked';
import { IconButton, Radio } from '@material-ui/core';
import Currency from '../../../../../components/Currency';
import formatCurrency from '../../../../../helpers/formatCurrency';
import { changeRecurringPaymentRowPriceField } from '../../../../../helpers/formInputHelpers';
import EditFilled from '../../../../../icons/EditFilled';
import { efesOrange } from '../../../../../theme';

import styles from './index.module.scss';

const handleSelectAllClick = ({ checked }, contract, updateContract) => {
  const { tableRows } = contract;

  const updatedRows = tableRows.map((row) => ({
    ...row,
    isChecked: checked ? true : row.isAlwaysChecked,
    isLastChecked: checked
      ? row.rowId === tableRows.length - 1
      : row.isAlwaysChecked && !row.isDisabled,
    payable: row.maxPayable,
  }));

  updateContract(updatedRows);
};

//= =================== HeaderCheckboxCell ====================
export const HeaderCheckboxCell = (cell, contract, updateContract, classes) => {
  const { tableRows } = contract;
  const { title } = cell;

  const allChecked = every(tableRows, (row) => row.isChecked);

  return (
    <label htmlFor={title}>
      <Checkbox
        icon={<CircleUnchecked className={styles.uncheckedIcon} />}
        checkedIcon={<CircleCheckedFilled className={styles.checkedIcon} />}
        id={title}
        checked={allChecked}
        onClick={(e) => handleSelectAllClick(e.target, contract, updateContract)}
        className={classes.headerCheckBox}
      />
      <Trans i18nKey={title} />
    </label>
  );
};

//= =================== CheckboxCell ====================
export const CheckboxCell = (row, cell, classes) => {
  const { title } = cell;
  const { isChecked, isDisabled } = row;

  return (
    !isDisabled && (
      <div id={styles.checkboxCell}>
        <p className={styles.expandTitle} role="presentation">
          <Trans i18nKey={title} />
        </p>
        <div className={styles.expandProperty}>
          <Radio
            checked={isChecked}
            disabled={isDisabled}
            checkedIcon={<CircleCheckedFilled className={styles.checkedIcon} />}
            icon={<CircleUnchecked className={styles.uncheckedIcon} />}
            className={classes.tdCheckbox}
          />
        </div>
      </div>
    )
  );
};

//= =================== IndicatorCell ====================
export const IndicatorCell = (row) => {
  const { isDisabled } = row;
  return (
    <div
      className={cs({
        [styles.indicatorCell]: true,
        [styles.disabled]: isDisabled,
      })}
    />
  );
};

//= =================== InputCell ====================
const handleInputChange = ({ value }, row, contract, updateContract) => {
  const valueToSet = changeRecurringPaymentRowPriceField(value, row);

  const { tableRows } = contract;

  const updatedRows = tableRows.map((item) => {
    if (item.rowId === row.rowId) {
      return { ...item, payable: valueToSet };
    }
    return item;
  });
  updateContract(updatedRows);
};

//= =================== MobileInputCell ====================
export const handleMobileInputChange = (value, row, setInputValue) => {
  const valueToSet = changeRecurringPaymentRowPriceField(value, row);

  setInputValue(valueToSet);
};

export const CurrencyCell = (row, cell) => {
  const { property } = cell;
  return (
    <p className={cs(styles.expandProperty)}>
      {/* eslint-disable-next-line react/destructuring-assignment */}
      <Currency value={row[property]} withCurrencyLabel />
    </p>
  );
};

export const InputCell = (row, cell, contract, updateContract) => {
  let content = '';
  const { maxPayable, payable, isLastChecked } = row;
  const { title } = cell;

  if (maxPayable === 0) {
    content = <CurrencyCell row={row} cell={cell} />;
  } else {
    content = (
      <div className={`${styles.expandProperty} ${styles.editableRow}`}>
        {isLastChecked ? (
          <Input
            size="mini"
            type="tel"
            value={payable}
            onChange={(evt, data) => handleInputChange(data, row, contract, updateContract)}
            onClick={(e) => {
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
            }}
            className={cs(styles.rowinput, {
              [styles.inputFocused]: isLastChecked,
            })}
            disabled={!isLastChecked}
            placeholder="0"
          />
        ) : <Currency value={payable} styling={{ amount: styles.amount }} />}
        <span>
          &nbsp;
          <Trans i18nKey="currency.AMD" />
        </span>
      </div>
    );
  }

  return (
    <div id={styles.inputCell}>
      <p className={styles.expandTitle} role="presentation">
        <Trans i18nKey={title} />
      </p>
      {content}
    </div>
  );
};

//= =================== PayableMobileCell ====================
export const PayableMobileCell = (row, cell, setEditableRowId, setEditableColumn, setOpen) => {
  const { maxPayable, payable, isLastChecked } = row;

  if (maxPayable === 0) {
    return <CurrencyCell row={row} cell={cell} />;
  }

  const handleClick = (e) => {
    if (isLastChecked) {
      setEditableRowId(row);
      setEditableColumn(cell);
      setOpen(true);
    }
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
  };

  return (
    <>
      <div
        className={cs({
          [styles.mobilePayable]: true,
          [styles.isNotChecked]: !isLastChecked,
        })}
      >
        {formatCurrency(payable, 0, 2)}
        <span>
          &nbsp;
          <Trans i18nKey="currency.AMD" />
          &nbsp;
        </span>
        {isLastChecked && (
          <IconButton
            onClick={(e) => {
              handleClick(e);
            }}
            className={styles.editButton}
          >
            <EditFilled color={efesOrange} />
          </IconButton>
        )}
      </div>
    </>
  );
};

//= =================== AmountMobileCell ====================
export const AmountMobileCell = (row) => (
  <div className={styles.mobileAmount}>
    {formatCurrency(row, 0, 2)}
    <span>
        &nbsp;
      <Trans i18nKey="currency.AMD" />
        &nbsp;
    </span>
  </div>
);
